import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { Container } from '../layout/Container';
import { Reveal } from '../motion/Reveal';
import { getIcon } from '../../lib/lucide-icons';
import { cn } from '../../lib/cn';
import { easeStripe } from '../../lib/motion';

export interface ToolData {
  title: string;
  description: string;
  iconName: string;
  href: string;
  badge?: string | null;
  metric?: string | null;
  metricLabel?: string | null;
}

export interface ToolsForAudienceProps {
  eyebrow?: string;
  title: string;
  description?: string;
  tools: ToolData[];
  tone?: 'brand' | 'lavender' | 'sage' | 'lavender-700' | 'brand-700' | 'accent';
}

const TONE_ICON_BG: Record<NonNullable<ToolsForAudienceProps['tone']>, string> = {
  brand: 'bg-brand-50 text-brand-700',
  lavender: 'bg-lavender-50 text-lavender-600',
  sage: 'bg-sage-100 text-sage-500',
  'lavender-700': 'bg-lavender-50 text-lavender-700',
  'brand-700': 'bg-brand-50 text-brand-800',
  accent: 'bg-accent-50 text-accent-700',
};

const TONE_ACCENT: Record<NonNullable<ToolsForAudienceProps['tone']>, string> = {
  brand: 'text-brand-700',
  lavender: 'text-lavender-700',
  sage: 'text-sage-500',
  'lavender-700': 'text-lavender-800',
  'brand-700': 'text-brand-800',
  accent: 'text-accent-800',
};

export function ToolsForAudience({
  eyebrow = 'Vos outils',
  title,
  description,
  tools,
  tone = 'brand',
}: ToolsForAudienceProps) {
  if (!tools || tools.length === 0) return null;

  return (
    <section className="bg-stone-50/50 py-16 md:py-20 lg:py-24">
      <Container>
        <Reveal>
          <div className="mx-auto mb-12 max-w-2xl text-center md:mb-14">
            <p className="eyebrow justify-center">{eyebrow}</p>
            <h2 className="mt-3 h-section">{title}</h2>
            {description && (
              <p className="mt-3 text-[15px] text-stone-600 leading-relaxed">{description}</p>
            )}
          </div>
        </Reveal>

        <div className="mx-auto grid max-w-6xl gap-3 sm:grid-cols-2 lg:grid-cols-3 lg:gap-4">
          {tools.map((tool, idx) => {
            const Icon = getIcon(tool.iconName);
            return (
              <motion.a
                key={tool.title}
                href={tool.href}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.35, delay: idx * 0.04, ease: easeStripe }}
                whileHover={{ y: -2 }}
                className="group relative flex flex-col gap-4 rounded-xl border border-stone-200/70 bg-paper p-5 shadow-[0_1px_2px_rgba(10,10,10,.04)] transition-all duration-150 ease-out hover:border-stone-300 hover:shadow-elevated"
              >
                <div className="flex items-start justify-between gap-3">
                  <div
                    className={cn(
                      'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-transform duration-150 ease-out group-hover:scale-105',
                      TONE_ICON_BG[tone],
                    )}
                  >
                    <Icon className="h-4 w-4" strokeWidth={1.7} />
                  </div>
                  {tool.badge && (
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
                        tool.badge.toLowerCase() === 'nouveau' || tool.badge.toLowerCase() === 'live'
                          ? 'bg-accent-50 text-accent-800 ring-1 ring-inset ring-accent-200/50'
                          : 'bg-stone-100 text-stone-600',
                      )}
                    >
                      {tool.badge.toLowerCase() === 'live' && (
                        <span className="h-1 w-1 rounded-full bg-accent-500 animate-glow-pulse" />
                      )}
                      {tool.badge}
                    </span>
                  )}
                </div>

                <div>
                  <p className="h-card">{tool.title}</p>
                  <p className="mt-1.5 text-xs text-stone-500 leading-relaxed">
                    {tool.description}
                  </p>
                </div>

                {/* Metric + arrow */}
                <div className="mt-auto flex items-center justify-between border-t border-stone-200/60 pt-3">
                  {tool.metric ? (
                    <div>
                      <p className={cn('metric-md', TONE_ACCENT[tone])}>{tool.metric}</p>
                      {tool.metricLabel && (
                        <p className="text-[10px] text-stone-500">{tool.metricLabel}</p>
                      )}
                    </div>
                  ) : (
                    <span />
                  )}
                  <span
                    className={cn(
                      'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-stone-100 transition-all duration-150 ease-out group-hover:bg-ink group-hover:text-ivory-50',
                    )}
                  >
                    <ArrowUpRight className="h-3 w-3" strokeWidth={2} />
                  </span>
                </div>
              </motion.a>
            );
          })}
        </div>
      </Container>
    </section>
  );
}
